import React, { useState, useEffect } from "react";
import { Mail, Lock, KeyRound, ArrowRight, RefreshCw, Clock, Edit2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useAuth from "../contexts/AuthContext";
import { useToast } from "../common_components/Toast";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { apiRequest, user } = useAuth();
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  // Resend OTP countdown timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendTimer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email) {
      setErrors({ email: "Email is required" });
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      const res = await apiRequest("/auth/forgot-password/send-otp", {
        method: "POST",
        body: JSON.stringify({ email: formData.email }),
      });

      if (res.success) {
        setStep(2);
        setResendTimer(45);
        toast.success("OTP sent to your email! Check spam folder if not found.");
      } else {
        const msg = res.message || res.error || "Failed to send OTP";
        setErrors({ submit: msg });
        toast.error(msg);
      }
    } catch (err: any) {
      const msg = err.message || "An error occurred while sending OTP";
      setErrors({ submit: msg });
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0 || isResending) return;

    setIsResending(true);
    setErrors({});
    try {
      const res = await apiRequest("/auth/forgot-password/send-otp", {
        method: "POST",
        body: JSON.stringify({ email: formData.email }),
      });

      if (res.success) {
        setResendTimer(45);
        toast.success("A new OTP has been generated and sent to your email!");
      } else {
        const msg = res.message || res.error || "Failed to resend OTP";
        setErrors({ submit: msg });
        toast.error(msg);
      }
    } catch (err: any) {
      const msg = err.message || "An error occurred while resending OTP";
      setErrors({ submit: msg });
      toast.error(msg);
    } finally {
      setIsResending(false);
    }
  };

  const handleChangeEmail = () => {
    setStep(1);
    setFormData((prev) => ({ ...prev, otp: "" }));
    setErrors({});
    setResendTimer(0);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.otp || !formData.newPassword || !formData.confirmPassword) {
      setErrors({
        otp: !formData.otp ? "OTP is required" : "",
        newPassword: !formData.newPassword ? "New password is required" : "",
        confirmPassword: !formData.confirmPassword ? "Confirm password is required" : "",
      });
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setErrors({
        confirmPassword: "Passwords do not match",
      });
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      const res = await apiRequest("/auth/forgot-password/verify-otp", {
        method: "POST",
        body: JSON.stringify({
          email: formData.email,
          otp: formData.otp,
          newPassword: formData.newPassword,
        }),
      });

      if (res.success) {
        toast.success("Password reset successful! You can now log in.");
        navigate("/login");
      } else {
        const msg = res.message || res.error || "Failed to reset password";
        setErrors({ submit: msg });
        toast.error(msg);
      }
    } catch (err: any) {
      const msg = err.message || "An error occurred while resetting password";
      setErrors({ submit: msg });
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-indigo-50 flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-32 w-80 h-80 bg-blue-300 rounded-full opacity-30 blur-3xl"></div>
        <div className="absolute -bottom-40 -left-32 w-80 h-80 bg-indigo-300 rounded-full opacity-30 blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="bg-white/80 backdrop-blur-sm border border-slate-200 rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-lg mb-4">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Forgot Password
            </h1>
            <p className="text-slate-600 text-sm">
              {step === 1
                ? "Enter your email to receive an OTP"
                : "Enter OTP and create new password"}
            </p>
          </div>

          {step === 1 ? (
            <form onSubmit={handleSendOtp} className="space-y-6">
              {/* Email Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50/50 transition-all ${
                      errors.email
                        ? "border-red-300 focus:ring-red-500"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Submit Error */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-700 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    {errors.submit}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-400 text-white font-bold py-3 px-4 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:shadow-none group"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Sending OTP...
                  </>
                ) : (
                  <>
                    Send OTP
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6">
              {/* Target Email Info Badge */}
              <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-3 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-blue-900 font-medium truncate">
                  <Mail className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="truncate">OTP sent to: <strong>{formData.email}</strong></span>
                </div>
                <button
                  type="button"
                  onClick={handleChangeEmail}
                  className="text-xs text-blue-700 hover:text-blue-900 font-semibold flex items-center gap-1 flex-shrink-0 transition-colors ml-2"
                >
                  <Edit2 className="w-3 h-3" /> Change
                </button>
              </div>

              {/* OTP Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Verification Code (OTP)
                </label>
                <input
                  name="otp"
                  type="text"
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50/50 transition-all ${
                    errors.otp
                      ? "border-red-300 focus:ring-red-500"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  value={formData.otp}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                {errors.otp && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                    {errors.otp}
                  </p>
                )}

                {/* Resend / Regenerate OTP Section */}
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 mt-2 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-600 font-medium flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      OTP delayed or not received?
                    </span>

                    {resendTimer > 0 ? (
                      <span className="text-slate-500 font-medium bg-slate-200/70 px-2 py-0.5 rounded-md">
                        Resend in <strong className="text-blue-600">{resendTimer}s</strong>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={isResending}
                        className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-800 disabled:text-slate-400 transition-colors bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-lg border border-blue-200"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isResending ? "animate-spin" : ""}`} />
                        {isResending ? "Sending..." : "Regenerate OTP"}
                      </button>
                    )}
                  </div>

                  <p className="text-[11px] text-slate-500 leading-normal">
                    Check your spam folder. If the OTP email is delayed, click <strong>"Regenerate OTP"</strong> above to send a fresh code.
                  </p>
                </div>
              </div>

              {/* New Password Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  New Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    name="newPassword"
                    type="password"
                    placeholder="Enter new password"
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50/50 transition-all ${
                      errors.newPassword
                        ? "border-red-300 focus:ring-red-500"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    value={formData.newPassword}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </div>
                {errors.newPassword && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                    {errors.newPassword}
                  </p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-slate-50/50 transition-all ${
                      errors.confirmPassword
                        ? "border-red-300 focus:ring-red-500"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              {/* Submit Error */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-700 flex items-center gap-2">
                    <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                    {errors.submit}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-slate-400 disabled:to-slate-400 text-white font-bold py-3 px-4 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:shadow-none group"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    Resetting Password...
                  </>
                ) : (
                  <>
                    Reset Password
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          )}

          {/* Back to login */}
          <div className="mt-8 text-center">
            <p className="text-sm text-slate-600">
              Remembered your password?{" "}
              <button
                onClick={() => navigate("/login")}
                className="font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                disabled={isLoading}
              >
                Sign in
              </button>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-slate-500">
            © 2025 AIIMS Mangalagiri. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
